import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const DIAGRAM_INNER_BOX_BG = "#F4F5F780";

export const useStyles = makeStyles((theme: Theme) => ({
  pageWrapper: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    direction: "ltr",
  },

  layoutFrame: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "760px",
    minWidth: "595px",
    gap: "16px",
    opacity: 1,
    boxSizing: "border-box",
    flexShrink: 0,
  },

  departmentTabsRow: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    flexShrink: 0,
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "4px",
    overflow: "hidden",
    direction: "ltr",
  },

  departmentTabsWrapper: {
    width: "100%",
  },

  departmentTabs: {
    width: "100%",
    minHeight: "42px",
    "& .MuiTabs-scroller": {
      overflow: "visible !important",
    },
    "& .MuiTabs-flexContainer": {
      width: "100%",
      display: "flex",
    },
    "& .MuiTab-root": {
      minHeight: "42px",
      height: "42px",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "21px",
      letterSpacing: 0,
      textTransform: "none",
      color: theme.palette.secondary.main,
      padding: "0 6px",
      margin: 0,
      minWidth: 0,
      maxWidth: "none",
      flex: "1 1 0",
      whiteSpace: "nowrap",
      overflow: "visible",
      fontFamily: theme.typography.fontFamily,
      position: "relative",
    },
    "& .MuiTab-wrapper, & .MuiTab-root > *": {
      whiteSpace: "nowrap",
      overflow: "visible",
    },
    "& .Mui-selected": {
      color: `${theme.palette.primary.main} !important`,
      fontWeight: 600,
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "2px",
        backgroundColor: theme.palette.primary.main,
      },
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-scrollButtons": {
      display: "none",
    },
  },

  departmentTabsWrapperEn: {
    width: "100%",
    overflow: "hidden",
  },

  departmentTabsEn: {
    width: "100%",
    minHeight: "42px",
    "& .MuiTabs-scroller": {
      overflowX: "auto !important",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    "& .MuiTabs-flexContainer": {
      display: "flex",
      width: "max-content",
    },
    "& .MuiTab-root": {
      minHeight: "42px",
      height: "42px",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "21px",
      letterSpacing: 0,
      textTransform: "none",
      color: theme.palette.secondary.main,
      padding: "0 16px",
      margin: 0,
      minWidth: "auto",
      maxWidth: "none",
      whiteSpace: "nowrap",
      fontFamily: theme.typography.fontFamily,
      position: "relative",
    },
    "& .Mui-selected": {
      color: `${theme.palette.primary.main} !important`,
      fontWeight: 600,
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "2px",
        backgroundColor: theme.palette.primary.main,
      },
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-scrollButtons": {
      display: "none",
    },
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    width: "100%",
    height: "42px",
    flexShrink: 0,
  },

  exportButton: {
    height: "42px",
    minHeight: "42px",
    minWidth: "unset",
    padding: "10px 16px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "140%",
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    boxShadow: "none",
    flexShrink: 0,
    "&:hover": {
      boxShadow: "none",
    },
  },

  pageTabs: {
    minHeight: "42px",
    maxWidth: "calc(100% - 136px)",
    backgroundColor: theme.palette.grey[400],
    borderRadius: "4px",
    padding: "0 8px",
    boxSizing: "border-box",
    "& .MuiTabs-scroller": {
      overflowX: "auto !important",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    "& .MuiTab-root": {
      minHeight: "42px",
      height: "42px",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "21px",
      textTransform: "none",
      color: theme.palette.text.primary,
      padding: "0 12px",
      minWidth: "unset",
      flexShrink: 0,
      whiteSpace: "nowrap",
      fontFamily: theme.typography.fontFamily,
    },
    "& .Mui-selected": {
      color: `${theme.palette.primary.main} !important`,
      fontWeight: 600,
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
  },

  pageTabLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },

  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "16px",
    padding: "12px",
    borderRadius: "4px",
    backgroundColor: theme.palette.background.paper,
    boxSizing: "border-box",
  },

  infoRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    width: "100%",
    flexShrink: 0,
    alignItems: "stretch",
  },

  infoCardWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
    width: "100%",
  },

  infoCard: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    padding: "12px",
    borderRadius: "4px",
    backgroundColor: DIAGRAM_INNER_BOX_BG,
    boxSizing: "border-box",
    minWidth: 0,
    width: "100%",
    textAlign: "right",
  },

  infoCardFull: {
    width: "100%",
    flexShrink: 0,
  },

  infoCardWrapperAuto: {
    flexShrink: 0,
  },

  infoCardAuto: {
    flex: "0 0 auto",
    alignItems: "flex-start",
  },

  cardTitle: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "140%",
    color: theme.palette.secondary.main,
    textAlign: "right",
    width: "100%",
  },

  fieldsGrid: {
    display: "grid",
    gridTemplateColumns: "auto auto",
    gap: "0 24px",
    width: "fit-content",
    maxWidth: "100%",
    marginLeft: "auto",
    marginRight: 0,
    direction: "rtl",
  },

  fieldsGridWide: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    direction: "rtl",
  },

  fieldsGridSingle: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    direction: "rtl",
  },

  fieldsGridColumnFlow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "0 48px",
    width: "100%",
    direction: "rtl",
  },

  fieldsColumn: {
    display: "flex",
    flex: 1,
    minWidth: 0,
    justifyContent: "flex-start",
    direction: "rtl",
  },

  fieldsList: {
    display: "grid",
    gridTemplateColumns: "auto auto",
    columnGap: "10px",
    rowGap: 0,
    gridAutoRows: "42px",
    alignItems: "center",
    direction: "rtl",
    width: "fit-content",
    maxWidth: "100%",
  },

  fieldLabel: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1,
    color: theme.palette.text.primary,
    textAlign: "right",
    whiteSpace: "nowrap",
    margin: 0,
  },

  fieldValue: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: 1,
    color: theme.palette.secondary.main,
    textAlign: "right",
    whiteSpace: "nowrap",
    margin: 0,
  },

  fieldValueWrap: {
    whiteSpace: "normal",
  },

  diagramImageSection: {
    position: "relative",
    width: "100%",
    flexShrink: 0,
    aspectRatio: "563 / 326",
  },

  diagramImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },

  customNotesSection: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
    flexShrink: 0,
  },

  customNotesList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
  },

  customNoteItem: {
    display: "flex",
    alignItems: "center",
    height: "42px",
    minHeight: "42px",
    padding: "10px",
    borderRadius: "4px",
    backgroundColor: theme.palette.background.paper,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.grey[300],
    boxSizing: "border-box",
    direction: "rtl",
    opacity: 1,
  },

  customNoteText: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1,
    color: theme.palette.text.primary,
    textAlign: "right",
    width: "100%",
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
